<template>
    <BaseModal
        center
        :is-open="modalStore.modals.calculate"
        @close="closeModal">
        <div
            class="h-full transform-gpu rounded-20 bg-gradient-to-tr from-accent to-white/20 max-md:my-5">
            <div class="relative px-3 pb-3 pt-10 md:p-10 lg:p-20">
                <AtomTitle class="max-md:max-w-[17.5rem] max-md:text-20/120">
                    Оставить заявку на&nbsp;предварительный расчет сметы
                </AtomTitle>
                <form class="flex flex-col pt-3 md:pt-6 lg:pt-10">
                    <div
                        class="flex max-lg:flex-col max-lg:space-y-5 lg:space-x-5">
                        <div
                            class="w-full max-w-[30rem] transform-gpu rounded-8 bg-gradient-to-tr p-3 max-lg:max-w-none">
                            <h2
                                class="text-16/148 font-semibold md:text-24/100">
                                Вы выбрали:
                            </h2>
                            <div
                                v-if="modalStore.calculatorSelectedValues"
                                class="pt-5">
                                <div
                                    v-for="field of calculator.fields_modal.filter(
                                        (field: ICalculatorFieldModal) =>
                                            field.prevent_hidden
                                                ? true
                                                : !hiddenFields[field.slug]
                                    )"
                                    :key="field.slug"
                                    class="flex flex-col">
                                    <div
                                        class="flex items-center justify-between">
                                        <div class="flex items-center">
                                            <p
                                                v-if="field.type === 'counter'"
                                                class="mr-3 size-7 shrink-0 content-center rounded-4 bg-white text-center text-12/160 font-medium text-accent">
                                                {{ selectedValues[field.slug] }}
                                            </p>
                                            <div>
                                                <p
                                                    v-html="
                                                        getFieldSummaryTitle(
                                                            field
                                                        )
                                                    "
                                                    class="text-12/160 font-medium md:text-16/148"></p>
                                                <p
                                                    v-if="
                                                        field.type === 'select'
                                                    "
                                                    class="text-12/100 italic md:text-14/148">
                                                    {{
                                                        getSelectValue(
                                                            field.slug,
                                                            selectedValues[
                                                                field.slug
                                                            ] as string
                                                        )
                                                    }}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            v-if="
                                                calculator.default_values[
                                                    field.slug
                                                ] !== selectedValues[field.slug]
                                            "
                                            aria-label="сбросить значение поля"
                                            @click.prevent="resetValue(field)">
                                            <AtomIcon name="close" />
                                        </button>
                                    </div>
                                    <AtomHr class="px-0 py-2" />
                                </div>
                            </div>
                            <div
                                v-html="totalPrice"
                                class="pt-3 text-20/120 font-semibold text-ash-200 md:text-24/133"></div>
                        </div>
                        <div class="flex w-full flex-col space-y-5">
                            <AtomField
                                v-model="formData.name"
                                placeholder="Ваше имя"
                                :error="errors.name"
                                @input="errors.name = ''">
                            </AtomField>
                            <AtomField
                                v-model="formData.phone"
                                placeholder="Телефон"
                                :error="errors.phone"
                                @input="
                                    validatePhone(formData, errors, $event)
                                ">
                            </AtomField>
                            <AtomField
                                v-model="formData.email"
                                placeholder="Электронная почта"
                                :error="errors.email"
                                @input="validateEmail(formData, errors)">
                            </AtomField>
                            <AtomTextarea
                                v-model="formData.message"
                                placeholder="Комментарий"
                                :error="errors.message"
                                @input="errors.message = ''"
                                class="w-full"></AtomTextarea>
                        </div>
                    </div>
                    <div
                        class="flex items-center pt-5 max-md:flex-col max-md:space-y-5 md:space-x-5 lg:ml-auto">
                        <AtomButton
                            class="w-full"
                            @click="handleForm">
                            Отправить
                        </AtomButton>

                        <BaseCheckbox
                            required
                            :error="errors.termsAccepted"
                            v-model="formData.termsAccepted"
                            class="!w-full shrink-0 md:max-w-[220px]"
                            @click="errors.termsAccepted = false">
                            <span
                                class="inline-block text-12/140 text-ash-200 max-md:text-left">
                                Я даю&nbsp;согласие на
                                <NuxtLink
                                    target="_blank"
                                    href="/pdf/soglasie.pdf"
                                    class="underline underline-offset-4">
                                    обработку персональных данных
                                </NuxtLink>
                            </span>
                        </BaseCheckbox>
                    </div>
                </form>
                <div
                    :style="`height: ${heightErrorBlock}px`"
                    class="overflow-hidden pt-2 transition-all duration-200 md:pt-5">
                    <div
                        ref="blockMessageError"
                        class="rounded-8 border border-red-600 bg-red-600/20 px-4 py-3 backdrop-blur-sm">
                        <p class="text-center text-12/140">
                            Чтобы отправить форму, нужно заполнить все поля
                        </p>
                    </div>
                </div>
                <button
                    class="absolute right-1 top-1 z-10 size-14 max-md:size-10 md:right-5 md:top-5"
                    @click="closeModal">
                    <svg
                        viewBox="0 0 70 70"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        width="70"
                        height="70"
                        class="size-14 max-md:size-10">
                        <path
                            d="M48.5 48.5L21 21"
                            stroke="white"></path>
                        <path
                            d="M48.5 21L21 48.5"
                            stroke="white"></path>
                    </svg>
                </button>
            </div>
        </div>
    </BaseModal>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import type {
    ICalculatorFieldModal,
    ICalculatorOption,
    ICalculatorSelectedValues,
    ICalculatorSetting,
    ICalculatorSettingField,
} from '~/types/calculator.types.ts'

import AtomButton from '~/components/atom/atom-button/AtomButton.vue'
import AtomField from '~/components/atom/atom-field/AtomField.vue'
import AtomHr from '~/components/atom/atom-hr/AtomHr.vue'
import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'
import AtomTextarea from '~/components/atom/atom-textarea/AtomTextarea.vue'
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import BaseCheckbox from '~/components/base/base-checkbox/BaseCheckbox.vue'
import BaseModal from '~/components/base/base-modal/BaseModal.vue'

import { errorHandling } from '~/utils/error-handling'
import { validateEmail, validatePhone } from '~/utils/validations'

const props = defineProps({
    calculator: {
        type: Object as PropType<ICalculatorSetting>,
        required: true,
    },
    hiddenFields: {
        type: Object as PropType<{ [key: string]: boolean }>,
        required: true,
    },
    totalPrice: {
        type: String as PropType<string>,
        required: true,
    },
})

const selectedValues = defineModel({
    type: Object as PropType<ICalculatorSelectedValues>,
    required: true,
})

const emit = defineEmits(['handleUpdate'])

const modalStore = useModalStore()
const blockMessageError = ref<HTMLElement | null>(null)

const formData = reactive({
    name: '',
    phone: '',
    email: '',
    message: '',
    termsAccepted: false,
})

const errors = reactive({
    name: '',
    phone: '',
    email: '',
    message: '',
    termsAccepted: false,
})

const heightErrorBlock = computed<number>(() => {
    if (!blockMessageError.value) return 0

    return errors.name || errors.phone || errors.email || errors.message
        ? blockMessageError.value.getBoundingClientRect().height + 20
        : 0
})

function getSelectValue(slug: string, value: string): string {
    const field = props.calculator.fields.find(
        (field: ICalculatorSettingField) => field.slug === slug
    )
    if (!field?.options) return ''

    const option = field.options.find(
        (option: ICalculatorOption) => option.calculator_variable_name === value
    )

    return option?.name || ''
}

function getFieldTitle(slug: string): string {
    const field = props.calculator.fields.find(
        (field: ICalculatorSettingField) => field.slug === slug
    )

    return field?.title || ''
}

function getFieldSummaryTitle(
    field: ICalculatorFieldModal
): string | undefined {
    const title = getFieldTitle(field.slug)
    if (!title) return undefined

    return title + (field.type === 'select' ? ':' : '')
}

function resetValue(field: ICalculatorFieldModal): void {
    if (!field.slug) return

    selectedValues.value[field.slug] =
        props.calculator.default_values[field.slug]
    emit('handleUpdate', field.slug)
}

function getOrder() {
    const values = []

    const fields: ICalculatorFieldModal[] =
        props.calculator.fields_modal.filter((field: ICalculatorFieldModal) =>
            field.prevent_hidden ? true : !props.hiddenFields[field.slug]
        )

    for (const field of fields) {
        values.push({
            title: getFieldTitle(field.slug),
            value:
                field.type === 'select'
                    ? getSelectValue(
                          field.slug,
                          selectedValues.value[field.slug] as string
                      )
                    : selectedValues.value[field.slug],
        })
    }

    const text =
        values.map((field) => `${field.title}: ${field.value}`).join('\n') +
        `\n\nИтоговая предварительная стоимость: ${props.totalPrice}`

    return text.replaceAll('<br/>', ' ').replaceAll('&nbsp;', ' ')
}
const route = useRoute()

async function handleForm(e: FormDataEvent) {
    e.preventDefault()
    validateData()

    if (
        !errors.name.length &&
        !errors.phone.length &&
        !errors.email.length &&
        !errors.message.length
    ) {
        console.log(route.query)
        const utmParams = useCookie<Record<string, any> | null>('utm')

        const { error } = await useApiFetch('/v1/callbacks', {
            method: 'POST',
            key: `callback-${Date.now()}`,
            body: JSON.stringify({
                name: formData.name,
                phone: formData.phone,
                email: formData.email,
                message: formData.message,
                form: 'Калькулятор',
                order: getOrder(),
                href: location.href,
            }),
            query: utmParams.value || undefined,
        })

        if (error.value && error.value?.data) {
            errorHandling(error.value, errors)

            return
        }

        closeModal()
    }
}

function validateData() {
    errors.name = !formData.name.length ? 'Заполните поле' : ''

    if (!formData.phone.length) {
        errors.phone = 'Заполните поле'
    }

    errors.message = !formData.message.length ? 'Заполните поле' : ''

    if (!formData.termsAccepted) {
        errors.termsAccepted = true
    }

    if (!formData.email.length) {
        errors.email = 'Заполните поле'
    } else {
        const isEmail = new RegExp(/^\S+@\S+\.\S+$/, 'i')
        errors.email = isEmail.test(formData.email)
            ? ''
            : 'Неверный формат почты'
    }
}

function clearErrors() {
    errors.name = ''
    errors.phone = ''
    errors.email = ''
    errors.message = ''
    errors.termsAccepted = false
}

function closeModal() {
    modalStore.close('calculate')
    clearErrors()
}
</script>
